/**
 * Anbindung der Planungsphase an den SiGe-Plan-Assistenten
 * (docs/planungsphase-automatisierung-plan.md, Stufe 3.2).
 *
 * Der Assistent liest den Projektsteckbrief (`services/projektSteckbrief`)
 * und macht daraus VORSCHLÄGE: die Baubeschreibung für Schritt 2, die
 * Merkmale für die Vorauswahl der Musterzeilen (E6) und die bedingten
 * mitgeltenden Dokumente, die bejahten Anhang-II-Nummern für die
 * Vollständigkeitsprüfung und die beantworteten Fragen mit Zielobjekt
 * `sigeplan` als Hinweisliste.
 *
 * ═══ NUR LESEN, ÜBERNAHME NUR PER KLICK ═══════════════════════════════════
 * Wie `useProjektSteckbrief`: hier wird nichts angelegt und nichts in den
 * Plan geschrieben. Jede Übernahme (Beschreibung, Dokument, „berücksichtigt")
 * löst der Nutzer im jeweiligen Schritt aus.
 *
 * ═══ WARUM EIN EIGENER HOOK ═══════════════════════════════════════════════
 * `SteckbriefFrage` trägt den Code, nicht die id der Planungsfrage. Der
 * Stempel „berücksichtigt" (`markiereUebernommen`) braucht aber die id. Der
 * Hook lädt deshalb dieselben Quellen (`ladeSteckbriefQuellen`, EIN Leseweg),
 * baut daraus den Steckbrief (`baueSteckbrief`) und behält nebenbei die
 * Zuordnung Code → id. Alles Übrige ist das Muster von `useProjektSteckbrief`
 * (Projekt im Zustand statt Lädt-Flagge, Projektdaten im useMemo gemischt,
 * `standTag` beim Eintreffen festgehalten).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReportMetadata } from '../../types';
import type { FrageKritikalitaet, FrageStatus, Planungsfrage, ProjektMerkmale } from '../planungsphase/types';
import { heuteLokalIso } from '../planungsphase/planungLogic';
import {
    baueSteckbrief,
    ladeSteckbriefQuellen,
    lesefehlerText,
    type ProjektSteckbrief,
    type SteckbriefFrage,
    type SteckbriefQuellen,
} from '../../services/projektSteckbrief';

// ─── Pure Teile ──────────────────────────────────────────────────────────────

/** Eine beantwortete Planungsfrage mit Zielobjekt SiGe-Plan — als Hinweis im Assistenten. */
export interface SigeHinweis {
    code: string;
    frage: string;
    antwort: string;
    kritikalitaet: FrageKritikalitaet;
    status: FrageStatus;
    /** Schon als berücksichtigt gestempelt (`planungsfragen.uebernommen_am`). */
    uebernommenAm: string | null;
    /** id der Planungsfrage für den Stempel; `null`, wenn der Code nicht aufzulösen ist. */
    frageId: string | null;
}

/** Zuordnung Code → id der Planungsfragen; bei doppeltem Code gewinnt die erste. */
export const frageIdsNachCode = (fragen: readonly Pick<Planungsfrage, 'id' | 'code'>[]): Map<string, string> => {
    const m = new Map<string, string>();
    (fragen || []).forEach(f => {
        if (f?.code && f?.id && !m.has(f.code)) m.set(f.code, f.id);
    });
    return m;
};

/**
 * Hinweise aus den Fragen mit Zielobjekt `sigeplan`: nur solche MIT Antwort —
 * eine offene Frage ist noch kein Hinweis für den Plan — und nicht
 * gegenstandslose. Noch nicht berücksichtigte stehen vorn, sonst bleibt die
 * Reihenfolge der Maske (`sortiereFragen` im Steckbrief).
 */
export const hinweiseAusFragen = (
    fragen: readonly SteckbriefFrage[],
    ids: ReadonlyMap<string, string>,
): SigeHinweis[] => {
    const liste = (fragen || [])
        .filter(f => f.status !== 'gegenstandslos' && (f.antwort || '').trim() !== '')
        .map((f): SigeHinweis => ({
            code: f.code,
            frage: f.frage,
            antwort: f.antwort.trim(),
            kritikalitaet: f.kritikalitaet,
            status: f.status,
            uebernommenAm: f.uebernommen_am || null,
            frageId: ids.get(f.code) ?? null,
        }));
    const offen = liste.filter(h => !h.uebernommenAm);
    const erledigt = liste.filter(h => !!h.uebernommenAm);
    return [...offen, ...erledigt];
};

/** Vorschlag für die Baubeschreibung in Schritt 2 — Text, Herkunft und Vorbehalt. */
export interface BeschreibungVorschlag {
    text: string;
    quelle: string;
    /** Nur bei `ungeklaert` gesetzt, z. B. „Nur aus Projektdaten — …". */
    grund: string | null;
}

export const baubeschreibungVorschlag = (sb: ProjektSteckbrief | null | undefined): BeschreibungVorschlag | null => {
    const a = sb?.baubeschreibung;
    const text = (a?.wert || '').trim();
    if (!a || !text) return null;
    return { text, quelle: a.quelle, grund: a.stand === 'belegt' ? null : (a.grund || null) };
};

/** Zutreffende Anhang-II-Nummern — nur die bejahten, nie die offenen. */
export const anhang2NummernAus = (sb: ProjektSteckbrief | null | undefined): number[] =>
    [...(sb?.anhang2.nummern.wert || [])];

/** Lage der Planungsphase aus Sicht des Assistenten. */
export type PlanungStand = 'laedt' | 'keine' | 'vorhanden';

// ─── Hook ────────────────────────────────────────────────────────────────────

interface Gelesen {
    fuer: string;
    quellen: SteckbriefQuellen;
    standTag: string;
}

export interface PlanungAnbindung {
    stand: PlanungStand;
    /** `null`, solange nicht geladen. */
    steckbrief: ProjektSteckbrief | null;
    /** Merkmale der Planungsphase; `null` ohne Planungsphase oder vor dem Laden (Verhalten wie bisher). */
    merkmale: ProjektMerkmale | null;
    hinweise: SigeHinweis[];
    beschreibung: BeschreibungVorschlag | null;
    anhang2Nummern: number[];
    /** Lesefehler als Satz — die Vorschläge liegen trotzdem vor (fail-open), mit `ungeklaert`. */
    fehler: string | null;
    /** Planungsdaten neu lesen — nach „berücksichtigt" oder Änderungen in der Planungsphase. */
    neuLaden: () => void;
}

export const usePlanungFuerSigePlan = (
    projectId: string,
    metadata: Partial<ReportMetadata> | null,
): PlanungAnbindung => {
    const [gelesen, setGelesen] = useState<Gelesen | null>(null);
    const [lauf, setLauf] = useState(0);

    const neuLaden = useCallback(() => setLauf(n => n + 1), []);

    useEffect(() => {
        let gilt = true;
        // `ladeSteckbriefQuellen` lehnt nie ab (fail-open); Lesefehler stehen
        // in den Quellen. Die Projektdaten kommen unten im `useMemo` dazu.
        void ladeSteckbriefQuellen(projectId, null).then(quellen => {
            if (gilt) setGelesen({ fuer: projectId, quellen, standTag: heuteLokalIso() });
        });
        return () => { gilt = false; };
    }, [projectId, lauf]);

    const bereit = gelesen !== null && gelesen.fuer === projectId;

    const steckbrief = useMemo(
        () => (bereit && gelesen ? baueSteckbrief({ ...gelesen.quellen, metadata }, gelesen.standTag) : null),
        [bereit, gelesen, metadata],
    );

    return useMemo((): PlanungAnbindung => {
        const vorhanden = !!steckbrief?.planungsphase.vorhanden;
        const ids = bereit && gelesen ? frageIdsNachCode(gelesen.quellen.fragen) : new Map<string, string>();
        return {
            stand: !bereit ? 'laedt' : vorhanden ? 'vorhanden' : 'keine',
            steckbrief,
            merkmale: steckbrief && vorhanden ? steckbrief.merkmale : null,
            hinweise: steckbrief ? hinweiseAusFragen(steckbrief.fragen.sigeplan, ids) : [],
            beschreibung: baubeschreibungVorschlag(steckbrief),
            anhang2Nummern: anhang2NummernAus(steckbrief),
            fehler: bereit && gelesen ? lesefehlerText(gelesen.quellen.lesefehler) : null,
            neuLaden,
        };
    }, [bereit, gelesen, steckbrief, neuLaden]);
};
